import { LessonPlan } from './../_models/lesson-plan';
import { LessonPlanStepStatus } from '../_models/lesson-plan-step-status';
import {DbService,  LESSON_PLAN_STORE_NAME,
   LESSON_PLAN_STEP_STATUS_STORE_NAME,
   LESSON_PLAN_STORE_TOPICID_INDEX,
   LESSON_PLAN_STORE_BATCHID_INDEX,
   LESSON_PLAN_STEP_STATUS_LESSONPLANID_INDEX,
   LESSON_PLAN_STEP_STATUS_SYNC_STATUS_INDEX } from './DbService';

// Cache TTL: 30 minutes in milliseconds
const CACHE_DURATION_MS = 30 * 60 * 1000;

class LessonPlanService {

  dbservice:DbService = new DbService()

  constructor(){
    this.dbservice.connect()
  }

  /**
   * Add lesson plans with cache timestamp
   */
  addLessonPlans(lessonPlans: LessonPlan[]): any {
    // Add cachedAt timestamp to each lesson plan for TTL tracking
    const timestamp = Date.now();
    const lessonPlansWithTimestamp = lessonPlans.map(lp => ({
      ...lp,
      cachedAt: timestamp
    }));
    return this.dbservice.saveAll(LESSON_PLAN_STORE_NAME, lessonPlansWithTimestamp);
  }

  /**
   * Get lesson plans for batch with TTL check
   * Returns null if cache is expired (older than 30 min)
   */
  async getLessonPlansForBatch(batchId: number): Promise<LessonPlan[] | null> {
    const lessonPlans = await this.dbservice.getByIndex(
      LESSON_PLAN_STORE_NAME,
      LESSON_PLAN_STORE_BATCHID_INDEX,
      batchId
    );

    if (!lessonPlans || lessonPlans.length === 0) {
      return null;
    }

    // Check if any lesson plan is older than TTL
    const now = Date.now();
    const oldestPlan = lessonPlans.find((lp: LessonPlan) => {
      if (!lp.cachedAt) return true; // No timestamp = treat as expired
      const age = now - lp.cachedAt;
      return age > CACHE_DURATION_MS;
    });

    if (oldestPlan) {
      const ageMinutes = oldestPlan.cachedAt
        ? Math.round((now - oldestPlan.cachedAt) / 60000)
        : 'unknown';
      console.log(`Lesson plan cache expired - age: ${ageMinutes} minutes`);
      return null; // Signal that cache is stale
    }

    return lessonPlans;
  }

  /**
   * Clear all cached lesson plans (for manual refresh)
   */
  async clearAllLessonPlans(): Promise<void> {
    try {
      await this.dbservice.connect();
      await this.dbservice.clearStore(LESSON_PLAN_STORE_NAME);
      console.log('Cleared lesson plan cache');
    } catch (error) {
      console.error('Error clearing lesson plan cache:', error);
    }
  }

  getLessonPlansForTopic(topicId: number): Promise<LessonPlan[]> {
    return this.dbservice.getByIndex(LESSON_PLAN_STORE_NAME, LESSON_PLAN_STORE_TOPICID_INDEX, topicId);
  }

  addLessonPlanStepStatuses(lessonPlanStepStatuses: LessonPlanStepStatus[]): any {
    return this.dbservice.saveAll(LESSON_PLAN_STEP_STATUS_STORE_NAME, lessonPlanStepStatuses);
  }

  saveOrUpdateLessonPlanStepStatus(lessonPlanStepStatus: LessonPlanStepStatus): any {
    return this.dbservice.save(LESSON_PLAN_STEP_STATUS_STORE_NAME, lessonPlanStepStatus);
  }

  getLessonPlanStepStatusesForLessonPlan(lessonPlanId: number): Promise<LessonPlanStepStatus[]> {
    return this.dbservice.getByIndex(LESSON_PLAN_STEP_STATUS_STORE_NAME, LESSON_PLAN_STEP_STATUS_LESSONPLANID_INDEX, lessonPlanId);
  }

  getLessonPlanStepStatusesBySyncStatus(syncStatus: number): Promise<LessonPlanStepStatus[]> {
    return this.dbservice.getByIndex(LESSON_PLAN_STEP_STATUS_STORE_NAME, LESSON_PLAN_STEP_STATUS_SYNC_STATUS_INDEX, syncStatus);
  }
}

export default LessonPlanService